import { format, isValid, parseISO } from 'date-fns'
import type { FmsNotificationInboxItem } from 'hooks/useFmsNotifications'
import { getColorByColorName } from 'pages/TaskScheduler/TaskSchedulerUtils'

function parseNotificationDate(createdAt?: string): Date | null {
  if (!createdAt) {
    return null
  }
  const normalized = createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T')
  const d = parseISO(normalized)
  return isValid(d) ? d : null
}

/** Matches API `format_notification_timestamp` (dd.MM.yyyy HH:mm:ss, local). */
export function formatNotificationTime(createdAt?: string): string {
  const d = parseNotificationDate(createdAt)
  if (!d) {
    return ''
  }
  return format(d, 'dd.MM.yyyy HH:mm:ss')
}

/** Date and time on separate lines for narrow history columns. */
export function formatNotificationTimeParts(createdAt?: string): { date: string; time: string } | null {
  const d = parseNotificationDate(createdAt)
  if (!d) {
    return null
  }
  return {
    date: format(d, 'dd.MM.yyyy'),
    time: format(d, 'HH:mm:ss'),
  }
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  'robot.diagnostics.error': 'Diagnostics',
  'robot.offline': 'Robot offline',
  'robot.online': 'Robot online',
  'robot.battery.low': 'Battery low',
  'robot.service.fms_command': 'Service command',
  'robot.charge.started': 'Charge started',
  'robot.charge.finished': 'Charge finished',
  'robot.charge.plate_lost': 'Charging plate lost',
  'robot.charge.stalled': 'Charging stalled',
  'station.occupied': 'Station occupied',
  'station.released': 'Station released',
  'station.stage.fill_started': 'Station fill started',
  'station.stage.fill_completed': 'Station fill completed',
  'station.stage.dock_finalize_succeeded': 'Washing started',
  'station.stage.undock_prepare_started': 'Washing completed',
  'odyssey.execution_started': 'Task started',
  'odyssey.execution_completed': 'Task completed',
  'odyssey.execution_paused': 'Task paused',
  'odyssey.execution_resumed': 'Task resumed',
  'odyssey.execution_canceled': 'Task canceled',
  'odyssey.execution_not_started': 'Task not started',
  'odyssey.scheduled_canceled': 'Schedule canceled',
  'gausium.incident': 'Gausium incident',
  'gausium.task_report.error': 'Gausium task error',
}

export function notificationEventTypeLabel(eventType?: string): string {
  if (!eventType) {
    return '—'
  }
  return EVENT_TYPE_LABELS[eventType] || eventType
}

/** Strip «Notify — » / «FMS Notify — » for prefs / history UI labels. */
export function notifyGroupDisplayName(name: string): string {
  const trimmed = name.replace(/\s+/g, ' ').trim()
  const withoutPrefix = trimmed.replace(/^(?:fms\s+)?notify\s*[—–-]\s*/i, '').trim()
  return withoutPrefix || trimmed
}

/** Full static catalog (tests / fallback). Prefer membership-filtered options in UI. */
export const NOTIFICATION_EVENT_TYPE_FILTER_OPTIONS = [
  { value: '', text: 'All types' },
  ...Object.entries(EVENT_TYPE_LABELS).map(([value, text]) => ({ value, text })),
]

export function notificationEventTypeFilterOptions(availableTypes: string[]): { value: string; text: string }[] {
  const known = availableTypes
    .map(value => ({ value, text: notificationEventTypeLabel(value) }))
    .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }))
  return [{ value: '', text: 'All types' }, ...known]
}

export function resolveRobotGroupLabels(item: {
  robot_id?: number | null
  robot_name?: string | null
  robot_serial?: string | null
  robot_color?: string | null
  payload?: { robot_name?: string; robot_serial?: string; robot_color?: string; [key: string]: unknown }
}): { name: string; serial: string; color: string } {
  const name =
    (item.robot_name || '').trim() ||
    (typeof item.payload?.robot_name === 'string' ? item.payload.robot_name.trim() : '') ||
    (item.robot_id != null ? `Robot #${item.robot_id}` : 'Robot')
  const serial =
    (item.robot_serial || '').trim() ||
    (typeof item.payload?.robot_serial === 'string' ? item.payload.robot_serial.trim() : '')
  const color =
    (item.robot_color || '').trim() ||
    (typeof item.payload?.robot_color === 'string' ? item.payload.robot_color.trim() : '')
  return { name, serial, color }
}

function payloadStationPk(payload?: { station_pk?: unknown; [key: string]: unknown }): number | null {
  const raw = payload?.station_pk
  if (raw == null || raw === '') {
    return null
  }
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function resolveStationGroupLabels(item: {
  station_pk?: number | null
  station_id?: string | null
  station_name?: string | null
  payload?: { station_pk?: unknown; station_id?: string; station_name?: string; [key: string]: unknown }
}): { name: string; serial: string; pk: number | null } {
  const pk = item.station_pk ?? payloadStationPk(item.payload)
  const name =
    (item.station_name || '').trim() ||
    (typeof item.payload?.station_name === 'string' ? item.payload.station_name.trim() : '') ||
    (pk != null ? `Station #${pk}` : 'Station')
  const serial =
    (item.station_id || '').trim() ||
    (typeof item.payload?.station_id === 'string' ? item.payload.station_id.trim() : '')
  return { name, serial, pk }
}

export function formatNotificationRobotCell(item: {
  robot_id?: number | null
  robot_name?: string | null
  robot_serial?: string | null
  station_pk?: number | null
  station_id?: string | null
  station_name?: string | null
}): string {
  if (item.station_pk != null || (item.station_id || '').trim()) {
    const name = (item.station_name || '').trim() || 'Station'
    const serial = (item.station_id || '').trim()
    if (serial) {
      return `${name} · ${serial}`
    }
    return name
  }
  const { name, serial } = resolveRobotGroupLabels(item)
  if (!item.robot_id && !serial) {
    return '—'
  }
  if (serial) {
    return `${name} · ${serial}`
  }
  return name
}

export function notificationGroupKey(item: {
  robot_id?: number | null
  robot_serial?: string | null
  station_pk?: number | null
  station_id?: string | null
  station_name?: string | null
  odyssey_id?: number | null
  payload?: {
    robot_serial?: string
    station_pk?: unknown
    station_id?: string
    station_name?: string
    [key: string]: unknown
  }
}): string {
  // Station events first — occupy/release may also carry robot_id.
  const station = resolveStationGroupLabels(item)
  if (station.pk != null) {
    return `station:${station.pk}`
  }
  if (station.serial) {
    return `station-sn:${station.serial}`
  }
  if (item.robot_id != null) {
    return `robot:${item.robot_id}`
  }
  const serial = resolveRobotGroupLabels(item).serial
  if (serial) {
    return `robot-sn:${serial}`
  }
  if (item.odyssey_id != null) {
    return `odyssey:${item.odyssey_id}`
  }
  return 'other'
}

export function robotGroupAccentColor(item: {
  robot_id?: number | null
  robot_serial?: string | null
  robot_color?: string | null
  payload?: { robot_serial?: string; robot_color?: string; [key: string]: unknown }
}): string | undefined {
  const { color } = resolveRobotGroupLabels(item)
  if (!color) {
    return undefined
  }
  return getColorByColorName(color)
}

export function isRobotNotificationGroup(groupKey: string): boolean {
  return groupKey.startsWith('robot:') || groupKey.startsWith('robot-sn:')
}

export function isStationNotificationGroup(groupKey: string): boolean {
  return groupKey.startsWith('station:') || groupKey.startsWith('station-sn:')
}

function notificationSemanticBody(item: FmsNotificationInboxItem, serial: string): string {
  const body = (item.body || '').trim()
  if (!body || (serial && body === `Robot ${serial}`)) {
    return ''
  }
  return body
}

/**
 * Secondary line: event time, plus API body when present (diagnostics message, GS details, etc.).
 */
export function notificationSubtitle(item: FmsNotificationInboxItem, serial: string): string {
  const time = formatNotificationTime(item.created_at)
  const semantic = notificationSemanticBody(item, serial)
  if (time && semantic) {
    return `${time} · ${semantic}`
  }
  if (semantic) {
    return semantic
  }
  return time
}
